const React = require('react');
const Joi = require('joi');
const Moment = require('moment/moment');
const SurveyForm = require('../SurveyForm');

const FormConfig = module.exports = {
    formId: 'survey-create-form',

    rerenderDeps(props = {}) {

        return [
            props.onSubmit,
            props.csv,
            props.activeSearchFilter,
            props.badges,
            props.departments,
            props.interestsActiveFilter_survey,
            props.isCompany,
            props.isCommunity,
            props.isSchoolOnline,
            props.majors,
            props.officesList,
            props.onClickResetSearchFilters,
            props.renderFormBody,
            props.schoolRoleGroups,
            props.schoolRoles,
            props.searchResults,
            props.setCsv,
            props.yearsHiredList
        ];
    },

    buildFormConfig(props = {}) {

        const {
            onSubmit
        } = props;

        return {
            fields: FormConfig.fields.build(props),
            defaults: FormConfig.defaults.build(),
            schema: internals.schema,
            formOptions: {
                criteriaMode: 'all'
            },
            formId: FormConfig.formId,
            onSubmit,
            syncDefaults: false
        };
    },

    component: {
        buildProps(props = {}) {

            return {
                activeSearchFilter: props.activeSearchFilter,
                badges: props.badges,
                csv: props.csv,
                departments: props.departments,
                interestsActiveFilter_survey: props.interestsActiveFilter_survey,
                isCompany: props.isCompany,
                isCommunity: props.isCommunity,
                isSchoolOnline: props.isSchoolOnline,
                majors: props.majors,
                officesList: props.officesList,
                onClickResetSearchFilters: props.onClickResetSearchFilters,
                schoolRoleGroups: props.schoolRoleGroups,
                schoolRoles: props.schoolRoles,
                searchResults: props.searchResults,
                setCsv: props.setCsv,
                yearsHiredList: props.yearsHiredList
            };
        }
    },

    defaults: {
        build() {

            return internals.buildDefaults();
        }
    },

    fields: {
        build(props = {}) {

            return internals.buildFields(props);
        }
    },

    submit: {
        buildPayload(data = {}, props = {}) {

            const {
                csv
            } = props;

            return {
                ...data,
                selectedRole: FormConfig.normalizeSelectedRole(data.selectedRole),
                selectedUsers: FormConfig.normalizeSelectedUsers(data.selectedUsers),
                questions: FormConfig.normalizeQuestions(data.questions),
                csv
            };
        }
    },

    buildCopyQuestions(survey = {}) {

        if (!Array.isArray(survey.questions)) {
            return [];
        }

        return survey.questions.map((question) => ({
            id: '',
            text: question.text,
            answerType: question.answerType,
            answers: question.answers ? question.answers.map((answer) => ({
                text: answer.text
            })) : []
        }));
    },

    flattenErrors(errors, prefix = '') {

        if (!errors || typeof errors !== 'object') {
            return [];
        }

        return Object.entries(errors).flatMap(([key, value]) => {

            const path = prefix ? `${prefix}.${key}` : key;

            if (value && typeof value.message === 'string') {
                return [{
                    path,
                    label: FormConfig.getFieldLabel(path),
                    message: value.message
                }];
            }

            return FormConfig.flattenErrors(value, path);
        });
    },

    getFieldLabel(path) {

        const questionMatch = path.match(/^questions\.(\d+)(?:\.(.*))?$/);

        if (questionMatch) {
            const questionNumber = Number(questionMatch[1]) + 1;
            const childPath = questionMatch[2] || '';

            if (childPath.startsWith('answers.')) {
                const answerMatch = childPath.match(/^answers\.(\d+)(?:\.(.*))?$/);
                const answerNumber = Number(answerMatch?.[1] || 0) + 1;

                return `Question ${questionNumber}, answer ${answerNumber}`;
            }

            if (childPath === 'text') {
                return `Question ${questionNumber} text`;
            }

            if (childPath === 'answerType') {
                return `Question ${questionNumber} answer type`;
            }

            return `Question ${questionNumber}`;
        }

        return internals.fieldLabels[path] || path;
    },

    isSubmittable({
        csv,
        questions,
        selectedRole,
        selectedUsers,
        title
    } = {}) {

        const normalizedRole = FormConfig.normalizeSelectedRole(selectedRole);
        const hasTitle = !!String(title || '').trim();
        const hasQuestions = Array.isArray(questions) && questions.length > 0;
        const hasRole = !!normalizedRole;
        const hasRequiredAudience = normalizedRole === 'search'
            ? Array.isArray(selectedUsers) && selectedUsers.length > 0
            : hasRole;
        const hasCsvAudience = !!csv && hasRole;

        return hasTitle && hasQuestions && (hasRequiredAudience || hasCsvAudience);
    },

    normalizeQuestions(questions = []) {

        if (!Array.isArray(questions)) {
            return questions;
        }

        return questions.map((question = {}) => ({
            text: question.text,
            answerType: question.answerType,
            answers: Array.isArray(question.answers)
                ? question.answers.map((answer = {}) => ({
                    text: answer.text
                }))
                : []
        }));
    },

    normalizeSelectedRole(selectedRole) {

        if (selectedRole === null || typeof selectedRole === 'undefined') {
            return '';
        }

        return String(selectedRole);
    },

    normalizeSelectedUsers(selectedUsers) {

        if (!Array.isArray(selectedUsers)) {
            return selectedUsers;
        }

        return selectedUsers.map((userOrId) => {

            if (userOrId && typeof userOrId === 'object') {
                return userOrId.id;
            }

            return userOrId;
        });
    },

    schema: null
};

FormConfig.buildDefaults = FormConfig.defaults.build;
FormConfig.buildFields = FormConfig.fields.build;
FormConfig.buildComponentProps = FormConfig.component.buildProps;
FormConfig.buildSubmitPayload = FormConfig.submit.buildPayload;
FormConfig.buildSubmissionPayload = function buildSubmissionPayload(data = {}, csv) {

    return FormConfig.submit.buildPayload(data, { csv });
};

const internals = {};

internals.fieldLabels = {
    title: 'Survey title',
    description: 'Description',
    notifyUsers: 'Notify users',
    manuallyStop: 'Manually stop survey',
    selectedRole: 'Role to Send Surveys To',
    selectedUsers: 'Selected users',
    startDate: 'Start date',
    endDate: 'End date',
    questions: 'Questions'
};

internals.buildDefaults = () => ({
    title: '',
    description: '',
    notifyUsers: true,
    manuallyStop: false,
    startDate: Moment().format('YYYY-MM-DD'),
    endDate: Moment().add(1, 'days').add(1, 'month').format('YYYY-MM-DD'),
    startImmediately: false,
    selectedRole: '',
    selectedUsers: [],
    questions: []
});

internals.buildFieldConfigFields = () => ([
    {
        name: 'title',
        type: 'TextField'
    },
    {
        name: 'description',
        type: 'TextareaField'
    },
    {
        name: 'notifyUsers',
        type: 'CheckboxField'
    },
    {
        name: 'manuallyStop',
        type: 'CheckboxField'
    },
    {
        name: 'startDate',
        type: 'DateField'
    },
    {
        name: 'endDate',
        type: 'DateField'
    },
    {
        name: 'startImmediately',
        type: 'CheckboxField'
    },
    {
        name: 'selectedRole',
        type: 'SelectField'
    },
    {
        name: 'selectedUsers',
        type: 'MultiSelectField'
    },
    {
        name: 'questions',
        type: 'custom'
    }
]);

internals.buildFields = (props = {}) => {

    if (props.renderFormBody) {
        return [
            {
                type: 'custom',
                key: 'survey-create-form-body',
                render: props.renderFormBody
            }
        ];
    }

    if (!props.setCsv) {
        return internals.buildFieldConfigFields();
    }

    return [
        {
            type: 'custom',
            key: 'survey-create-form-body',
            componentProps: FormConfig.component.buildProps(props),
            render: internals.renderSurveyForm
        }
    ];
};

internals.renderSurveyForm = ({ componentProps, methods: formMethods }) => {

    const {
        control,
        formState: {
            errors
        },
        setValue,
        reset
    } = formMethods;

    return <SurveyForm
        control={control}
        setValue={setValue}
        csv={componentProps.csv}
        setCsv={componentProps.setCsv}
        reset={reset}
        errors={errors}
        onClickResetSearchFilters={componentProps.onClickResetSearchFilters}
        activeSearchFilter={componentProps.activeSearchFilter}
        interestsActiveFilter_survey={componentProps.interestsActiveFilter_survey}
        badges={componentProps.badges}
        searchResults={componentProps.searchResults}
        departments={componentProps.departments}
        officesList={componentProps.officesList}
        yearsHiredList={componentProps.yearsHiredList}
        isCompany={componentProps.isCompany}
        isCommunity={componentProps.isCommunity}
        isSchoolOnline={componentProps.isSchoolOnline}
        schoolRoles={componentProps.schoolRoles}
        schoolRoleGroups={componentProps.schoolRoleGroups}
        majors={componentProps.majors}
    />;
};

internals.answerSchema = Joi.object({
    id: Joi.any().optional().strip(),
    text: Joi.string().required().messages({
        'string.empty': 'Answer text is required',
        'any.required': 'Answer text is required'
    })
});

internals.schema = Joi.object({
    questions: Joi.array().items(
        Joi.object({
            id: Joi.any().optional().strip(),
            multipleAnswers: Joi.any().optional().strip(),
            freeInput: Joi.any().optional().strip(),
            text: Joi.string().required().messages({
                'string.empty': 'Question text is required',
                'any.required': 'Question text is required'
            }),
            answerType: Joi.string().valid('freeInput', 'select', 'checkbox'),
            answers: Joi.array().items(internals.answerSchema).when('answerType', {
                is: Joi.valid('select', 'checkbox'),
                then: Joi.array().items(internals.answerSchema).min(1).required().messages({
                    'array.min': 'At least one answer is required',
                    'any.required': 'At least one answer is required'
                }),
                otherwise: Joi.array().items(internals.answerSchema).optional()
            })
        })
    ).min(1).required().messages({
        'array.min': 'You need at least one question',
        'any.required': 'At least one question is required'
    }),
    title: Joi.string().required().messages({
        'string.empty': 'Survey title is required',
        'any.required': 'Survey title is required'
    }),
    description: Joi.string().allow('').optional(),
    notifyUsers: Joi.boolean(),
    manuallyStop: Joi.boolean(),
    startImmediately: Joi.boolean(),
    selectedRole: Joi.string().required().messages({
        'string.empty': 'Must select role or search based',
        'any.required': 'Must select role or search based'
    }),
    selectedUsers: Joi.array().when('selectedRole', {
        is: 'search',
        then: Joi.array().min(1).required().messages({
            'array.min': 'You need to select at least one user',
            'any.required': 'At least one user is required'
        }),
        otherwise: Joi.array().optional()
    }),
    startDate: Joi.date().empty('').required().messages({
        'date.base': 'The value must be a date (MM/DD/YYYY)',
        'any.required': 'Survey Start Date is required'
    }),
    endDate: Joi.any().when('manuallyStop', {
        is: false,
        then: Joi.date().empty('').required().messages({
            'date.base': 'The value must be a date (MM/DD/YYYY)',
            'any.required': 'Survey End Date is required'
        }),
        otherwise: Joi.any().optional()
    })
});

FormConfig.schema = internals.schema;
FormConfig.internals = internals;
